/**
 * AreaProfilePage — V2 orchestrator for the Area profile.
 *
 * Manages:
 *   - Session & permissions
 *   - Two polling cycles (basic data 600s, reporting data 1200s)
 *   - Tab state (basic, reporting, devices, wellness, logs)
 *   - Drawer/dialog state for all edit flows
 *   - Toast notifications
 *
 * Props:
 *   id — Area ID (from URL param)
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './area-profile-page.css';
import NavigationV2 from '../../../../components/v2/NavigationV2.jsx';
import ProfileBanner from '../../../servers/profile/components/ProfileBanner.jsx';
import BasicInfoTab from './components/BasicInfoTab.jsx';
import BasicInfoDrawer from './components/BasicInfoDrawer.jsx';
import ReportingTab from './components/ReportingTab.jsx';
import DevicesTab from './components/DevicesTab.jsx';
import DeviceAssignDrawer from './components/DeviceAssignDrawer.jsx';
import DeviceRemoveDialog from './components/DeviceRemoveDialog.jsx';
import WellnessTab from './components/WellnessTab.jsx';
import LogsTab from './components/LogsTab.jsx';
import EventNoteDialog from './components/EventNoteDialog.jsx';

import {
  getSession, getCampusId, getUserId, getSessionId,
  getCampusList, updateSessionCampus, getRoleWeight, getActiveVendorIdList,
  getCampusConfig,
} from '../../../../services/session.js';
import {
  fetchAreaProfile, fetchAreaTypes, fetchBuildings,
  fetchAreaDevices, fetchAvailableDevices, fetchAreaLogs,
  fetchCareflowWindows, fetchRoundsWindows, fetchPreformNotes,
  fetchHourChart, fetchWeekChart, fetchReasonSummary, fetchLimitedEvents,
  editAreaProfile, removeDeviceFromArea, assignDeviceToArea, addEventNote,
} from './api/areaProfileApi.js';
// Cross-domain import: speakers are sourced via the same getsipdevices proc
// the Devices page uses. Mirrors the merge added on AreasPage.jsx so SIP
// devices can be assigned to areas through this profile page too.
import { fetchSpeakerDevices } from '../../../devices/api/devicesApi.js';
import {
  iAmHigherAuthority as iAmHigherAuth,
  convertIsoToLocal,
  formatDateIso,
  getToday,
} from '../../../../libs.jsx';

// ==========================================================================
// COMPONENT
// ==========================================================================

const AreaProfilePage = ({ id }) => {
  const navigate = useNavigate();

  // --------------------------------------------------------------------------
  // SESSION
  // --------------------------------------------------------------------------
  const [campusId, setCampusId] = useState(() => getCampusId());
  const campusList = useMemo(() => getCampusList() || [], []);
  const campusName = useMemo(() => {
    const s = getSession();
    return s?.campusData?.[campusId.toString()]?.name || s?.campus || '';
  }, [campusId]);
  const myRoleWeight = useMemo(() => getRoleWeight(), []);
  const editRights = useMemo(() => iAmHigherAuth(20, myRoleWeight), [myRoleWeight]);
  const devEditRights = useMemo(() => iAmHigherAuth(10, myRoleWeight), [myRoleWeight]);
  const showAdminTabs = useMemo(() => iAmHigherAuth(80, myRoleWeight), [myRoleWeight]);
  // Grandstream-only: speakers come from a separate stored proc and are
  // merged into both the assigned and available device lists below.
  const isGrandstream = useMemo(() => Boolean(getCampusConfig()?.is_grandstream), [campusId]);
  // System (manufacture_id=1, device_category_id IN (4,5)) and SIP
  // (manufacture_id=10) devices may only be added/removed by role_weight > 80.
  // Mirrors AreasPage's pattern exactly: restricted devices stay VISIBLE in
  // the assigned list so caregivers see what's attached to the area, but the
  // remove button is gated, and the available pool hides them so they can't
  // be assigned in the first place.
  const canManageRestrictedDevices = useMemo(
    () => iAmHigherAuth(80, myRoleWeight),
    [myRoleWeight]
  );
  // System device types that don't have a per-device profile page and
  // shouldn't be managed by non-admins (repeaters, etc.). The
  // getareadevices proc doesn't reliably return device_category_id, so
  // we also match by device_type_id to keep parity with AreasPage —
  // otherwise restricted devices would slip through when cid is absent.
  const SYSTEM_RESTRICTED_TYPE_IDS = useMemo(() => new Set([10, 11, 56, 57]), []);
  const isRestrictedDevice = useCallback((d) => {
    const mid = parseInt(d?.manufacture_id, 10);
    const cid = parseInt(d?.device_category_id, 10);
    const tid = parseInt(d?.device_type_id, 10);
    if (mid === 10) return true;
    if (mid === 1 && (cid === 4 || cid === 5)) return true;
    if (mid === 1 && SYSTEM_RESTRICTED_TYPE_IDS.has(tid)) return true;
    return false;
  }, [SYSTEM_RESTRICTED_TYPE_IDS]);

  const areaId = useMemo(() => (id ? parseInt(id, 10) : 0), [id]);

  const getSessionParams = useCallback(() => {
    return {
      campusId,
      userId: getUserId(),
      clientId: getSessionId(),
      vendor_ids: getActiveVendorIdList(),
    };
  }, [campusId]);

  // Session feature flags
  const sessionRef = useRef(getSession());
  const eventNotesActive = sessionRef.current?.event_notes_active || false;
  const eventNotesPreform = sessionRef.current?.event_notes_preform || false;
  const eventNotesFreeform = sessionRef.current?.event_notes_freeform || false;
  const isReasonEditable = sessionRef.current?.is_reason_editable || false;
  const tzOffset = parseInt(sessionRef.current?.campusData?.[campusId?.toString()]?.tz_offset || 0);

  // --------------------------------------------------------------------------
  // TOAST
  // --------------------------------------------------------------------------
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const showToast = useCallback((type, message) => {
    clearTimeout(toastTimerRef.current);
    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), 6000);
  }, []);

  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  // --------------------------------------------------------------------------
  // TABS
  // --------------------------------------------------------------------------
  const [activeTab, setActiveTab] = useState('basic');

  // --------------------------------------------------------------------------
  // CORE STATE
  // --------------------------------------------------------------------------
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(true);

  // Basic area data
  const [basicData, setBasicData] = useState({});
  const [areaName, setAreaName] = useState('');
  const [areaTypeId, setAreaTypeId] = useState(0);
  const [areaTypeName, setAreaTypeName] = useState('');
  const [maxOccupancy, setMaxOccupancy] = useState(0);
  const [buildingId, setBuildingId] = useState(0);
  const [buildingName, setBuildingName] = useState('');
  const [residents, setResidents] = useState([]);
  const [areaGroups, setAreaGroups] = useState([]);

  // Dropdowns
  const [areaTypes, setAreaTypes] = useState([]);
  const [buildings, setBuildings] = useState([]);

  // Devices
  const [areaDevices, setAreaDevices] = useState([]);
  const [availableDevices, setAvailableDevices] = useState([]);

  // Wellness
  const [careData, setCareData] = useState([]);
  const [roundsData, setRoundsData] = useState([]);

  // Logs
  const [logData, setLogData] = useState([]);

  // Reporting
  const [hourData, setHourData] = useState({});
  const [weekData, setWeekData] = useState({});
  const [reasonData, setReasonData] = useState({});
  const [eventData, setEventData] = useState([]);
  const [prenoteData, setPrenoteData] = useState([]);

  // --------------------------------------------------------------------------
  // DRAWER / DIALOG STATE
  // --------------------------------------------------------------------------
  const [basicDrawerOpen, setBasicDrawerOpen] = useState(false);
  const [assignDrawerOpen, setAssignDrawerOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removeDevice, setRemoveDevice] = useState(null);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteEventIndex, setNoteEventIndex] = useState(null);
  const [saving, setSaving] = useState(false);

  // --------------------------------------------------------------------------
  // DATE RANGE (for event reason summary)
  // --------------------------------------------------------------------------
  const startDate = useMemo(() => formatDateIso(getToday(-28)), []);
  const endDate = useMemo(() => formatDateIso(getToday()), []);

  // --------------------------------------------------------------------------
  // POLLING CYCLE 1: Basic data (600 second interval)
  // --------------------------------------------------------------------------
  const fetchBasicDataCycle = useCallback(async () => {
    const baseFetches = [
      fetchAreaProfile(campusId, areaId),
      fetchAreaTypes(campusId),
      fetchBuildings(campusId),
      fetchAreaDevices(campusId, areaId),
      fetchAvailableDevices(campusId),
      fetchCareflowWindows(campusId),
      fetchRoundsWindows(campusId),
      showAdminTabs ? fetchAreaLogs(areaId) : Promise.resolve([]),
      // Speakers source — skipped (resolves to []) on non-grandstream
      // campuses so the proc isn't called pointlessly.
      isGrandstream ? fetchSpeakerDevices(campusId) : Promise.resolve([]),
    ];

    const [
      profileResult, typesResult, buildingsResult,
      devicesResult, availResult, careResult, roundsResult, logsResult,
      speakersResult,
    ] = await Promise.all(baseFetches);

    // Process profile
    if (profileResult?.length > 0) {
      const b = profileResult[0];
      setBasicData(b);
      setAreaName(b.area_name || '');
      setAreaTypeId(b.area_type_id || 0);
      setAreaTypeName(b.area_type_description || '');
      setMaxOccupancy(b.max_occupancy || 0);
      setBuildingId(b.building_id || 0);
      setBuildingName(b.building_name || '');
      setResidents(b.residents || []);
      setAreaGroups(b.groups || []);
    }

    // Merge speakers into both device lists with device_id dedup. Mirrors
    // the pattern added to AreasPage.jsx — the speaker stored proc returns
    // SIP devices the inovonics proc doesn't surface. For the per-area
    // "assigned" list, only include speakers whose area_id matches this
    // area (the available pool gets all speakers as candidates).
    const speakers = speakersResult || [];

    const mergedAvail = [...(availResult || [])];
    const availSeen = new Set(mergedAvail.map((d) => d.device_id));
    speakers.forEach((d) => {
      if (!availSeen.has(d.device_id)) {
        mergedAvail.push(d);
        availSeen.add(d.device_id);
      }
    });

    const mergedAreaDevices = [...(devicesResult || [])];
    const areaSeen = new Set(mergedAreaDevices.map((d) => d.device_id));
    speakers.forEach((d) => {
      if (Number(d.area_id) === areaId && !areaSeen.has(d.device_id)) {
        mergedAreaDevices.push(d);
        areaSeen.add(d.device_id);
      }
    });

    setAreaTypes(typesResult || []);
    setBuildings(buildingsResult || []);
    // Pass both lists through as-is. Restricted devices stay visible in the
    // assigned table for awareness; the DevicesTab disables the remove
    // button when canManageRestrictedDevices is false. The available list
    // is filtered down in DeviceAssignDrawer instead so a non-admin can't
    // assign a new restricted device.
    setAreaDevices(mergedAreaDevices);
    setAvailableDevices(mergedAvail);
    setCareData(careResult || []);
    setRoundsData(roundsResult || []);
    setLogData(logsResult || []);
    setLoading(false);
  }, [campusId, areaId, showAdminTabs, isGrandstream]);

  useEffect(() => {
    fetchBasicDataCycle();
    const interval = setInterval(fetchBasicDataCycle, 600_000);
    return () => clearInterval(interval);
  }, [fetchBasicDataCycle]);

  // --------------------------------------------------------------------------
  // POLLING CYCLE 2: Reporting data (1200 second interval)
  // --------------------------------------------------------------------------
  const fetchReportingCycle = useCallback(async () => {
    const [hourResult, weekResult, reasonResult, eventsResult, prenotes] = await Promise.all([
      fetchHourChart(campusId, areaId),
      fetchWeekChart(campusId, areaId),
      fetchReasonSummary(campusId, areaId, startDate, endDate),
      fetchLimitedEvents(campusId, areaId),
      (eventNotesPreform ? fetchPreformNotes(campusId) : Promise.resolve([])),
    ]);

    setHourData(hourResult || {});
    setWeekData(weekResult || {});
    setReasonData(reasonResult || {});
    setEventData(eventsResult || []);
    setPrenoteData(prenotes || []);
    setReportLoading(false);
  }, [campusId, areaId, startDate, endDate, eventNotesPreform]);

  useEffect(() => {
    fetchReportingCycle();
    const interval = setInterval(fetchReportingCycle, 1_200_000);
    return () => clearInterval(interval);
  }, [fetchReportingCycle]);

  // --------------------------------------------------------------------------
  // CAMPUS CHANGE HANDLER
  // --------------------------------------------------------------------------
  const handleCampusChange = useCallback((e) => {
    const val = parseInt(e.target.value, 10);
    updateSessionCampus(val);
    navigate('/community/areas');
  }, [navigate]);

  // --------------------------------------------------------------------------
  // ACTION HANDLERS
  // --------------------------------------------------------------------------

  /** Save basic info (name, type, building, occupancy) */
  const handleBasicInfoSave = useCallback(async (formData) => {
    setSaving(true);
    const session = getSessionParams();
    const payload = [{
      area_id: areaId,
      area_name: formData.areaName,
      area_type_id: parseInt(formData.areaTypeId),
      max_occupancy: parseInt(formData.maxOccupancy),
      building_id: parseInt(formData.buildingId),
      modified_by: getUserId(),
      campus_id: campusId,
      vendor_ids: session.vendor_ids,
    }];
    const oldPayload = [{
      area_id: areaId,
      area_name: areaName,
      area_type_id: areaTypeId,
      max_occupancy: maxOccupancy,
    }];

    const result = await editAreaProfile(payload, oldPayload, session);
    setSaving(false);

    if (result && result[0]) {
      showToast('success', 'Area information saved.');
      setBasicDrawerOpen(false);
      fetchBasicDataCycle();
    } else {
      showToast('error', `Error during save${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [areaId, areaName, areaTypeId, maxOccupancy, campusId, getSessionParams, showToast, fetchBasicDataCycle]);

  /** Remove a device from the area */
  const handleRemoveDevice = useCallback(async () => {
    if (!removeDevice) return;
    // Defense-in-depth: the remove button is already disabled in the UI for
    // restricted devices when the user can't manage them, but block the
    // mutation here too in case it's reached some other way.
    if (isRestrictedDevice(removeDevice) && !canManageRestrictedDevices) {
      showToast('error', 'You do not have permission to remove this device.');
      setRemoveDialogOpen(false);
      setRemoveDevice(null);
      return;
    }
    setSaving(true);
    const session = getSessionParams();
    const result = await removeDeviceFromArea(removeDevice, areaId, session, isGrandstream);
    setSaving(false);

    if (result && result[0]) {
      showToast('success', 'Device has been removed.');
      setRemoveDialogOpen(false);
      setRemoveDevice(null);
      fetchBasicDataCycle();
    } else {
      showToast('error', `Failed to remove device${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [removeDevice, areaId, getSessionParams, showToast, fetchBasicDataCycle, isRestrictedDevice, canManageRestrictedDevices]);

  /** Assign a device to the area */
  const handleAssignDevice = useCallback(async (device) => {
    // Defense-in-depth, matching the remove path above.
    if (isRestrictedDevice(device) && !canManageRestrictedDevices) {
      showToast('error', 'You do not have permission to assign this device.');
      return;
    }
    setSaving(true);
    const session = getSessionParams();
    const campusconfig = getCampusConfig();
    const result = await assignDeviceToArea(device, areaId, areaName, session, campusconfig?.is_grandstream);
    setSaving(false);

    if (result && result[0]) {
      showToast('success', `${device.device_name || 'Device'} has been assigned.`);
      setAssignDrawerOpen(false);
      fetchBasicDataCycle();
    } else {
      showToast('error', `Failed to assign device${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [areaId, areaName, getSessionParams, showToast, fetchBasicDataCycle, isRestrictedDevice, canManageRestrictedDevices]);

  /** Add event note */
  const handleAddNote = useCallback(async (eventId, noteText) => {
    setSaving(true);
    const session = getSessionParams();
    const noteItems = [{
      event_id: eventId,
      campus_id: campusId,
      note: noteText,
      modified_by: getUserId(),
    }];

    const result = await addEventNote(noteItems, session);
    setSaving(false);

    if (result && result[0] === true) {
      showToast('success', 'Note added successfully.');
      setNoteDialogOpen(false);
      setNoteEventIndex(null);
      fetchReportingCycle();
    } else {
      showToast('error', `Error adding note${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [campusId, getSessionParams, showToast, fetchReportingCycle]);

  /** Open remove device dialog */
  const openRemoveDialog = useCallback((device) => {
    setRemoveDevice(device);
    setRemoveDialogOpen(true);
  }, []);

  /** Open note dialog */
  const openNoteDialog = useCallback((eventIndex) => {
    setNoteEventIndex(eventIndex);
    setNoteDialogOpen(true);
  }, []);

  // --------------------------------------------------------------------------
  // DERIVED VALUES
  // --------------------------------------------------------------------------
  const bannerMeta = useMemo(() => [
    { label: '\u00A0', value: '\u00A0' },   // placeholder — reserved for future use
    { label: 'Building', value: buildingName },
    { label: 'Max Occupancy', value: maxOccupancy ? maxOccupancy.toString() : '—' },
  ], [buildingName, maxOccupancy]);

  // --------------------------------------------------------------------------
  // TAB LIST
  // --------------------------------------------------------------------------
  const tabs = useMemo(() => {
    const t = [
      { key: 'basic', label: 'Basic Info' },
      { key: 'reporting', label: 'Reporting' },
      { key: 'devices', label: 'Devices' },
      { key: 'wellness', label: 'Wellness' },
    ];
    if (showAdminTabs) {
      t.push({ key: 'logs', label: 'Logs' });
    }
    return t;
  }, [showAdminTabs]);

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------
  return (
    <>
      <NavigationV2 />
      <div className="nav2-main">
      <div className="dp-page">
        {/* Toast */}
        {toast && (
          <div className={`dp-toast dp-toast--${toast.type}`}>
            <span>{toast.message}</span>
            <button className="dp-toast__close" onClick={() => setToast(null)}>✕</button>
          </div>
        )}

        {/* Page Header */}
        <header className="dp-page-header">
          <div className="dp-page-header__inner">
            <div className="dp-breadcrumb-row">
              <nav className="dp-breadcrumb">
                <Link to="/community/areas" className="dp-breadcrumb__text">Areas</Link>
                <span className="dp-breadcrumb__separator">/</span>
                <span className="dp-breadcrumb__current">{areaName || 'Area Profile'}</span>
              </nav>
              <div className="dp-campus-row">
                <label htmlFor="ap-campus-selector" className="dp-campus-label">Campus:</label>
                {campusList.length > 1 ? (
                  <select
                    id="ap-campus-selector"
                    className="dp-campus-select"
                    value={campusId}
                    onChange={handleCampusChange}
                  >
                    {campusList.map((c) => (
                      <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
                    ))}
                  </select>
                ) : (
                  <span className="dp-campus-name">{campusName}</span>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Profile Banner */}
        <ProfileBanner
          name={areaName || ''}
          subtitle={areaTypeName}
          metaFields={bannerMeta}
          loading={loading}
        />

        {/* Tab Bar */}
        <div className="pp-tab-bar">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`pp-tab${activeTab === tab.key ? ' is-active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="pp-tab-content">
          {activeTab === 'basic' && (
            <BasicInfoTab
              areaName={areaName}
              areaTypeName={areaTypeName}
              buildingName={buildingName}
              maxOccupancy={maxOccupancy}
              residents={residents}
              areaGroups={areaGroups}
              loading={loading}
              editRights={editRights}
              onEdit={() => setBasicDrawerOpen(true)}
            />
          )}

          {activeTab === 'reporting' && (
            <ReportingTab
              hourData={hourData}
              weekData={weekData}
              reasonData={reasonData}
              eventData={eventData}
              eventNotesActive={eventNotesActive}
              isReasonEditable={isReasonEditable}
              tzOffset={tzOffset}
              loading={reportLoading}
              onAddNote={openNoteDialog}
              areaId={areaId}
              areaName={areaName}
            />
          )}

          {activeTab === 'devices' && (
            <DevicesTab
              areaDevices={areaDevices}
              areaName={areaName}
              devEditRights={devEditRights}
              onAssign={() => setAssignDrawerOpen(true)}
              onRemove={openRemoveDialog}
              canManageRestrictedDevices={canManageRestrictedDevices}
              isRestrictedDevice={isRestrictedDevice}
            />
          )}

          {activeTab === 'wellness' && (
            <WellnessTab
              careData={careData}
              roundsData={roundsData}
              areaGroups={areaGroups}
            />
          )}

          {activeTab === 'logs' && showAdminTabs && (
            <LogsTab
              logData={logData}
              tzOffset={tzOffset}
            />
          )}
        </div>

        {/* ---- Drawers ---- */}

        {/* Basic Info Drawer */}
        {basicDrawerOpen ? (
          <div className="dp-drawer-backdrop" onClick={() => setBasicDrawerOpen(false)} aria-hidden="true" />
        ) : null}
        <BasicInfoDrawer
          open={basicDrawerOpen}
          onClose={() => setBasicDrawerOpen(false)}
          onSave={handleBasicInfoSave}
          saving={saving}
          areaName={areaName}
          areaTypeId={areaTypeId}
          buildingId={buildingId}
          maxOccupancy={maxOccupancy}
          areaTypes={areaTypes}
          buildings={buildings}
        />

        {/* Device Assign Drawer */}
        {assignDrawerOpen ? (
          <div className="dp-drawer-backdrop" onClick={() => setAssignDrawerOpen(false)} aria-hidden="true" />
        ) : null}
        <DeviceAssignDrawer
          open={assignDrawerOpen}
          onClose={() => setAssignDrawerOpen(false)}
          onAssign={handleAssignDevice}
          saving={saving}
          availableDevices={availableDevices}
          areaDevices={areaDevices}
          areaName={areaName}
          canManageRestrictedDevices={canManageRestrictedDevices}
          isRestrictedDevice={isRestrictedDevice}
        />

        {/* Device Remove Dialog */}
        {removeDialogOpen && removeDevice && (
          <DeviceRemoveDialog
            deviceName={removeDevice.device_name || removeDevice.manufacture_uuid || 'Device'}
            areaName={areaName}
            onClose={() => { setRemoveDialogOpen(false); setRemoveDevice(null); }}
            onConfirm={handleRemoveDevice}
            saving={saving}
          />
        )}

        {/* Event Note Dialog */}
        {noteDialogOpen && noteEventIndex !== null && (
          <EventNoteDialog
            event={eventData[noteEventIndex]}
            prenoteData={prenoteData}
            eventNotesPreform={eventNotesPreform}
            eventNotesFreeform={eventNotesFreeform}
            tzOffset={tzOffset}
            onClose={() => { setNoteDialogOpen(false); setNoteEventIndex(null); }}
            onSave={handleAddNote}
            saving={saving}
          />
        )}
      </div>
      </div>
    </>
  );
};

export default AreaProfilePage;
